import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FIRELINE_MEDIC_BADGE } from '../brand/firelineMedicBadge';
import { getMemberAccessToken, supabase } from './supabase';
import './mobile-verification.css';

const REMS_SHARED_EMAIL = '24rem@metrofire.ca.gov';

function enabled() {
  // Mobile verification is a mandatory Line Medic 1.2 requirement.
  // Do not allow a stale Preview environment variable to silently bypass it.
  return true;
}

function isSharedRems(profile) {
  return String(profile?.email || '').trim().toLowerCase() === REMS_SHARED_EMAIL;
}

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 11);
}

function displayPhone(value) {
  const digits = digitsOnly(value);
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length <= 3) return local;
  if (local.length <= 6) return `(${local.slice(0,3)}) ${local.slice(3)}`;
  return `(${local.slice(0,3)}) ${local.slice(3,6)}-${local.slice(6,10)}`;
}

export function mobileVerificationEnforced() {
  return enabled();
}

export default function MobileVerificationGate({ profile, children, onProfileChanged }) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [stage, setStage] = useState('phone');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const enforced = enabled() && !isSharedRems(profile);
  const profileHasField = profile && Object.prototype.hasOwnProperty.call(profile, 'mobile_verified_at');
  const profileVerified = Boolean(profile?.mobile_e164 && profile?.mobile_verified_at);

  const headers = useCallback(async () => {
    const token = await getMemberAccessToken();
    if (!token) throw new Error('Member sign in required.');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  const loadStatus = useCallback(async () => {
    if (!enforced || profileVerified) return;
    try {
      const response = await fetch('/api/mobile-verification', { cache: 'no-store', headers: await headers() });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to check mobile verification.');
      setStatus(payload);
    } catch (statusError) {
      setError(statusError?.message || 'Unable to check mobile verification.');
    }
  }, [enforced, profileVerified, headers]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const verified = profileVerified || status?.verified === true;
  const providerReady = status?.providerReady === true;
  const phoneComplete = useMemo(() => {
    const digits = digitsOnly(phone);
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  }, [phone]);

  if (!enforced || verified) return typeof children === 'function' ? children(profile) : children;

  // This prevents a 1.2 build from accidentally locking members out if somebody turns
  // on the frontend flag before the matching database migration exists.
  if (!profileHasField) {
    return (
      <div className="mobile-verify-root"><section className="mobile-verify-card">
        <img src={FIRELINE_MEDIC_BADGE} alt="Line Medic" />
        <span className="mobile-verify-kicker">LINE MEDIC 1.2 SECURITY</span>
        <h1>Mobile verification is not ready</h1>
        <p>The 1.2 mobile-verification requirement is enabled in this build, but the matching account migration has not been activated.</p>
        <div className="mobile-verify-warning">Access stays blocked rather than guessing that an unverified number is trusted. Program Admin must finish the 1.2 backend cutover.</div>
      </section></div>
    );
  }

  const requestCode = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch('/api/mobile-verification', {
        method: 'POST',
        headers: await headers(),
        body: JSON.stringify({ action: 'request', phone }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to send verification code.');
      setChallengeId(payload?.challengeId || '');
      setMessage(`Verification code sent to ${payload?.mobile || displayPhone(phone)}.`);
      setStage('code');
    } catch (requestError) {
      setError(requestError?.message || 'Unable to send verification code.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const response = await fetch('/api/mobile-verification', {
        method: 'POST',
        headers: await headers(),
        body: JSON.stringify({ action: 'verify', phone, code, challengeId }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.verified !== true) throw new Error(payload?.error || 'The verification code could not be confirmed.');

      const { data, error: refreshError } = await supabase.rpc('refresh_member_approval');
      if (refreshError) throw refreshError;
      setStatus({ verified: true, mobile: payload?.mobile || '' });
      onProfileChanged?.(data || profile);
    } catch (verifyError) {
      setError(verifyError?.message || 'Unable to verify this mobile number.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mobile-verify-root">
      <section className="mobile-verify-card">
        <img src={FIRELINE_MEDIC_BADGE} alt="Line Medic" />
        <span className="mobile-verify-kicker">LINE MEDIC 1.2 SECURITY</span>
        <h1>Verify your mobile number</h1>
        <p>Mobile verification is required to continue in Line Medic 1.2. It will be used for account recovery, new-device verification, and security-sensitive features.</p>
        <div className="mobile-verify-note"><strong>Your Line Medic account stays the same.</strong><span>This adds a verified recovery number to your existing roster-approved account.</span></div>

        {status && !providerReady && <div className="mobile-verify-warning">The SMS service is not configured in this development build yet. There is intentionally no Skip option.</div>}
        {error && <div className="mobile-verify-error">{error}</div>}
        {message && <div className="mobile-verify-success">{message}</div>}

        {stage === 'phone' ? (
          <form onSubmit={requestCode}>
            <label>Mobile number<input type="tel" inputMode="tel" autoComplete="tel" value={displayPhone(phone)} onChange={event => setPhone(digitsOnly(event.target.value))} placeholder="(916) 555-1234" required /></label>
            <button type="submit" disabled={busy || !phoneComplete || !providerReady}>{busy ? 'Sending…' : 'Text Verification Code'}</button>
          </form>
        ) : (
          <form onSubmit={verifyCode}>
            <label>Verification code<input type="text" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={event => setCode(event.target.value.replace(/\D/g,'').slice(0,8))} placeholder="6-digit code" required /></label>
            <button type="submit" disabled={busy || code.length < 4}>{busy ? 'Verifying…' : 'Verify & Continue'}</button>
            <button type="button" className="mobile-verify-link" onClick={() => { setStage('phone'); setCode(''); setChallengeId(''); setMessage(''); }}>Use a different number</button>
          </form>
        )}

        <small>Program Admin can reset a verified number if your phone or number changes. A replacement number must still complete SMS verification.</small>
      </section>
    </div>
  );
}
